/**
 * API fetch helper — calls the service that owns /job-apply (conversations-service
 * today, job-apply-service after the cutover).
 *
 * This client is always cross-origin against that API, so the host-only auth
 * cookie is never sent along: the accessToken travels in the Authorization
 * header instead.
 */
import { getAccessToken, notifySessionExpired, renewSession } from '~/lib/auth';
import { getLocale } from '~/i18n/locale';
import { messages } from '~/i18n';
import { VIEW_AS_HEADER, appliesViewAs, currentViewAs, readOnlyResponse } from '~/lib/viewAs';

// Unset (the normal case) points at production: api.jobs.tbit.app, the host of
// job-apply-service. Explicitly empty means same-origin, which is how the Vite
// dev proxy in vite.config.ts is used — hence `??` and not `||`.
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'https://api.jobs.tbit.app';

/**
 * La petición no llegó al servidor: sin red, la conexión se cortó a mitad de
 * camino o el navegador no pudo leer lo que iba en el cuerpo (un archivo que
 * Android ya no deja abrir). `fetch` lo avisa con un TypeError cuyo texto es del
 * navegador y en inglés («Failed to fetch», «Load failed»), y las pantallas que
 * muestran `err.message` se lo ponían tal cual a la persona. Acá sale en el
 * idioma de la app; el error original queda en `original` para Sentry.
 */
export class NetworkError extends Error {
  readonly original: unknown;

  constructor(original: unknown) {
    super(messages().common.network.offline);
    this.name = 'NetworkError';
    this.original = original;
  }
}

/** Una cancelación (AbortController) no es un problema de red: sigue tal cual. */
function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const url = `${API_BASE}${path}`;
  // Same-origin (dev proxy) rides on the cookie; cross-origin needs the header.
  const sendsBearer = !!API_BASE;

  // «Ver como» (lib/viewAs.ts): las rutas del candidato van con la membresía
  // que se está mirando, y ninguna escritura sale de esta pestaña. El servidor
  // corta igual; esto evita que un botón llegue a intentarlo.
  const viewAs = appliesViewAs(path) ? currentViewAs() : null;
  if (viewAs) {
    const method = (init?.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') return readOnlyResponse();
  }

  const send = (token: string | null) => {
    const headers = new Headers(init?.headers);
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    // El idioma en que se está usando la app, en TODA petición: lo que el
    // servicio devuelva para mostrar (el coach del cuestionario, la
    // transcripción de voz) tiene que salir en ese idioma. Es un header
    // "safelisted" de CORS, así que no dispara preflight.
    if (!headers.has('Accept-Language')) headers.set('Accept-Language', getLocale());
    if (viewAs) headers.set(VIEW_AS_HEADER, viewAs.id);
    return fetch(url, { ...init, headers, credentials: 'include' }).catch((err: unknown) => {
      throw isAbort(err) ? err : new NetworkError(err);
    });
  };

  const staleToken = sendsBearer ? getAccessToken() : null;
  const response = await send(staleToken);

  // An expired access token is rejected before the route handler runs, so the
  // request had no effect and is safe to replay — including POSTs.
  if (response.status === 401 && sendsBearer) {
    const freshToken = await renewSession();
    if (freshToken) return send(freshToken);
    // Nothing left to try: let the UI offer a way out rather than reporting
    // this as a failure of whatever the user was doing.
    notifySessionExpired();
  }

  return response;
}
